import type { ReactNode } from "react";
import { useLayoutEffect, useMemo } from "react";
import SDKInit from "@/betby/components/SDKInit";
import BetbyScriptLoader from "@/betby/components/BetbyScriptLoader";
import {
  createCallbackHostBridge,
  type HostBridgeCallbacks,
} from "@/betby/adapters/host/callbackHostBridge";
import {
  resetHostBridge,
  registerHostBridge,
} from "@/betby/adapters/host/hostBridge";
import { windowHostBridge } from "@/betby/adapters/host/windowHostBridge";
import { AppConfigContext } from "@/betby/infrastructure/config/AppConfigContext";
import {
  registerAppConfig,
  resetAppConfig,
  resolveAppConfig,
  type AppConfigOverrides,
} from "@/betby/infrastructure/config/appConfig";
import { resetBetbyRendererScriptSrcCache } from "@/betby/infrastructure/betby/betbyRendererScriptResolver";
import { syncHttpClientConfig } from "@/betby/api/http";
import {
  registerSessionTicketRequestCallback,
  unregisterSessionTicketRequestCallback,
} from "@/betby/models/auth/sessionTicketCache";

/**
 * Runtime config injected by the host application.
 */
export interface BetbyConfig {
  apiBaseUrl?: string;
  storageKeyPrefix?: string;
  analyticsEndpoint?: string;
  analyticsProductId?: string;
  btRendererScriptUrl?: string;
  btRendererScriptUrlUS?: string;
}

/**
 * Top-level provider props for the embedded widget.
 */
export interface BetbyProviderProps extends HostBridgeCallbacks {
  children: ReactNode;
  config?: BetbyConfig;
}

function buildAppConfigOverrides(config?: BetbyConfig): AppConfigOverrides {
  return {
    api: {
      baseUrl: config?.apiBaseUrl,
    },
    analytics: {
      endpoint: config?.analyticsEndpoint,
      productId: config?.analyticsProductId,
    },
    betby: {
      rendererScriptUrl: config?.btRendererScriptUrl,
      rendererScriptUrlUS: config?.btRendererScriptUrlUS,
    },
    storage: {
      keyPrefix: config?.storageKeyPrefix,
    },
  };
}

export default function BetbyProvider({
  children,
  config,
  onBack,
  onRecharge,
  onLogin,
  onTokenExpired,
  onCurrencyChange,
  onGetWallet,
  onUrlChange,
  onNavigate,
  onRequestSessionTicket,
}: BetbyProviderProps) {
  const appConfigOverrides = useMemo(
    () => buildAppConfigOverrides(config),
    [config],
  );
  const resolvedConfig = useMemo(
    () => resolveAppConfig(appConfigOverrides),
    [appConfigOverrides],
  );

  const hostBridge = useMemo(() => {
    const hasCallbacks = [
      onBack,
      onRecharge,
      onLogin,
      onTokenExpired,
      onCurrencyChange,
      onGetWallet,
      onUrlChange,
      onNavigate,
      onRequestSessionTicket,
    ].some(Boolean);

    if (!hasCallbacks) {
      return windowHostBridge;
    }

    return createCallbackHostBridge({
      onBack,
      onRecharge,
      onLogin,
      onTokenExpired,
      onCurrencyChange,
      onGetWallet,
      onUrlChange,
      onNavigate,
      onRequestSessionTicket,
    });
  }, [
    onBack,
    onRecharge,
    onLogin,
    onTokenExpired,
    onCurrencyChange,
    onGetWallet,
    onUrlChange,
    onNavigate,
    onRequestSessionTicket,
  ]);

  useLayoutEffect(() => {
    registerAppConfig(appConfigOverrides);
    registerHostBridge(hostBridge);
    resetBetbyRendererScriptSrcCache();
    syncHttpClientConfig();

    // Register session ticket request callback if hostBridge supports it
    if (hostBridge.requestSessionTicket) {
      registerSessionTicketRequestCallback(hostBridge.requestSessionTicket);
    }

    return () => {
      unregisterSessionTicketRequestCallback();
      resetHostBridge();
      resetAppConfig();
      resetBetbyRendererScriptSrcCache();
      syncHttpClientConfig();
    };
  }, [appConfigOverrides, hostBridge]);

  return (
    <AppConfigContext.Provider value={resolvedConfig}>
      <SDKInit>
        {children}
        <BetbyScriptLoader />
      </SDKInit>
    </AppConfigContext.Provider>
  );
}
